import { useLocalStorage } from "react-use";

export type SortPreference = {
  sortField?: string;
  sortDesc?: boolean;
};

/**
 * Utility function to read sort preference from localStorage.
 * Can be used outside React components (e.g., in route loaders).
 *
 * @param tableId - The unique identifier for the table (e.g., "bookingsTable")
 * @returns The stored sort preference, or null if not found or invalid
 */
export function getStoredSortPreference(tableId: string): SortPreference | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(`table-sort-${tableId}`);
    if (!stored) return null;
    return JSON.parse(stored) as SortPreference;
  } catch {
    return null;
  }
}

/**
 * Hook to manage sort preference in localStorage per table.
 * Stores preference as `table-sort-${tableId}` (following the pattern used by other table settings).
 *
 * @param tableId - The unique identifier for the table (e.g., "bookingsTable")
 * @returns Tuple of [sortPreference, setSortPreference]
 * - sortPreference: Current sort preference for the table (sortField, sortDesc)
 * - setSortPreference: Function to update the sort preference
 *
 * @example
 * ```tsx
 * const [sortPref, setSortPref] = useSortPreference("bookingsTable");
 * ```
 */
export function useSortPreference(tableId: string): [SortPreference, (preference: SortPreference) => void] {
  const [storedSort, setStoredSort] = useLocalStorage<SortPreference>(`table-sort-${tableId}`, {});

  const sortPreference = storedSort || {};

  const setSortPreference = (preference: SortPreference) => {
    setStoredSort(preference);
  };

  return [sortPreference, setSortPreference];
}
